import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useNavigation } from '@/contexts/NavigationContext';

/**
 * Reads /chat/:conversationId from the URL on mount,
 * loads the conversation metadata, and sets the correct navigation context.
 * Only acts when the conversation isn't already active (i.e. cold open via URL).
 * If the conversation doesn't exist or isn't accessible, redirects to /.
 */
export function ConversationRouter() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const {
    activeConversationId,
    setActiveConversationId,
    navigateToPersonal,
    navigateToProject,
    navigateToGlobal,
    navigateToTab,
    skipNextConversationReset,
  } = useNavigation();
  const initialized = useRef(false);

  useEffect(() => {
    if (!conversationId || initialized.current) return;

    // If the conversation is already active in context, skip — this means
    // UrlSync pushed the URL after the user sent a message, not a cold open.
    if (activeConversationId === conversationId) {
      initialized.current = true;
      return;
    }

    initialized.current = true;

    (async () => {
      const { data, error } = await supabase
        .from('conversations')
        .select('id, project_id, scope')
        .eq('id', conversationId)
        .maybeSingle();

      if (error || !data) {
        navigate('/app', { replace: true });
        return;
      }

      skipNextConversationReset.current = true;

      if (data.project_id) {
        // Look up the project to determine whether it lives in the Personal or Global space
        const { data: project } = await supabase
          .from('projects')
          .select('scope_type')
          .eq('id', data.project_id)
          .maybeSingle();
        const isPersonalProject = project?.scope_type === 'personal';
        navigateToProject(data.project_id, isPersonalProject);
      } else if ((data as any).scope === 'global') {
        navigateToGlobal();
      } else {
        navigateToPersonal();
      }

      navigateToTab('chat');
      setActiveConversationId(data.id);
    })();
  }, [conversationId, activeConversationId, navigate, setActiveConversationId, navigateToPersonal, navigateToProject, navigateToGlobal, navigateToTab, skipNextConversationReset]);

  return null;
}
